import React from 'react';
import { ExternalLink, HeartPulse } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import {
  PageHero,
  FeatureGrid,
  SEOContent,
  CTASection
} from '../components/shared';
import { pageHeroImages } from '../data/mock';

const leistungenFeatures = [
  {
    id: '2A',
    title: 'Hausärztliche Leistungen',
    description:
      'Ihr erster Ansprechpartner für Vorsorge, akute Beschwerden und langfristige Betreuung.',
    image: '/images/2A_Hausarzt_Familie.webp',
    link: '/leistungen/hausarzt'
  },
  {
    id: '2B',
    title: 'Diagnostik-Zentrum',
    description:
      'Moderne Medizintechnik und schnelle Befunde direkt im Haus.',
    image: '/images/MRT 5.webp',
    link: '/leistungen/diagnostik'
  },
  {
    id: '2C',
    title: 'Kardiologie & Herz-Check',
    description:
      'Moderne Herzdiagnostik und persönliche Betreuung an einem Standort.',
    image: '/images/2C_Hero_Kardiologie.webp',
    link: '/leistungen/kardiologie'
  },
  {
    id: '2D',
    title: 'Vorsorge & Prävention',
    description:
      'Gesund bleiben durch Früherkennung, Beratung und regelmäßige Untersuchungen.',
    image: '/images/ganze Familie.webp',
    link: '/leistungen/vorsorge'
  },
  {
    id: '2E',
    title: 'DMP – Chronikerprogramme',
    description:
      'Strukturierte Betreuung bei chronischen Erkrankungen – persönlich und kontinuierlich.',
    image: '/images/Content_Behandlung_2.webp',
    link: '/leistungen/dmp'
  },
  {
    id: '2F',
    title: 'Schmerz & Wirbelsäule',
    description:
      'Gezielte Schmerztherapie, Wirbelsäulenbehandlung und Akupunktur.',
    image: '/images/2F_Hero_Schmerztherapie.webp',
    link: '/leistungen/schmerz'
  },
  {
    id: '2G',
    title: 'Individuelle Leistungen',
    description:
      'Individuelle Gesundheitsleistungen, Vorsorgeprogramme und ästhetische Behandlungen.',
    image: '/images/Behandlung88.webp',
    link: '/leistungen/spezial'
  },
  {
    id: '2H',
    title: 'Patienten-Service',
    description:
      'Organisation, Service und digitale Wege – einfach, sicher und persönlich.',
    image: '/images/4_Termin_Online.webp',
    link: '/leistungen/digital'
  }
];

export const LeistungenPage = () => {
  return (
    <Layout>
      <PageHero
        title="Medizinische Leistungen im Medicum Diez"
        subtitle="Hausärztliche Versorgung, moderne Diagnostik und spezialisierte Fachmedizin unter einem Dach."
        backgroundImage={pageHeroImages.leistungen}
        ctaText="Jetzt Termin vereinbaren"
      />

      <FeatureGrid
        title="Unsere Leistungsbereiche"
        features={leistungenFeatures}
        columns={4}
      />

      {/* Ergänzendes Angebot: Preventum Diez */}
      <section className="bg-[#f9f3e2] px-4 pb-16 md:pb-24 lg:px-8">
        <div className="container mx-auto">
          <a
            href="https://preventum-diez.de/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Preventum Diez – Prävention und Sportmedizin in neuem Tab öffnen"
            className="group relative block overflow-hidden rounded-3xl bg-[#032925] text-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
          >
            <div className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-[#8ae9bb]/10" />
            <div className="absolute -bottom-32 right-40 h-64 w-64 rounded-full bg-[#edd68d]/10" />

            <div className="relative grid gap-8 px-6 py-8 md:grid-cols-[1fr_auto] md:items-center md:px-10 md:py-10 lg:px-12">
              <div>
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#edd68d] text-[#032925]">
                    <HeartPulse className="h-6 w-6" />
                  </div>

                  <span className="text-sm font-semibold uppercase tracking-[0.14em] text-[#edd68d]">
                    Ergänzendes Gesundheitsangebot
                  </span>
                </div>

                <h2 className="mb-3 text-2xl font-semibold text-white md:text-3xl">
                  Preventum Diez
                </h2>

                <p className="max-w-3xl text-lg font-medium text-white/90">
                  Prävention, Sportmedizin und Leistungsdiagnostik
                </p>

                <p className="mt-3 max-w-3xl leading-relaxed text-white/75">
                  Für Menschen, die ihre Gesundheit, Belastbarkeit und
                  Leistungsfähigkeit gezielt untersuchen, erhalten und
                  verbessern möchten.
                </p>

                <div className="mt-5 flex flex-wrap gap-2">
                  <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-sm text-white/85">
                    Prävention
                  </span>

                  <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-sm text-white/85">
                    Sportmedizin
                  </span>

                  <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-sm text-white/85">
                    Leistungsdiagnostik
                  </span>
                </div>
              </div>

              <div className="flex md:justify-end">
                <span className="inline-flex items-center gap-2 rounded-full bg-[#edd68d] px-6 py-3 font-semibold text-[#032925] transition-colors group-hover:bg-[#d4c17d]">
                  Preventum kennenlernen
                  <ExternalLink className="h-4 w-4" />
                </span>
              </div>
            </div>
          </a>
        </div>
      </section>

      <SEOContent
        title="Medizinische Leistungen im Rhein-Lahn-Kreis"
        content="Im Medicum Diez finden Sie ein breites medizinisches Leistungsspektrum: Hausärztliche Versorgung, Vorsorge, moderne Bildgebung, Herzdiagnostik, Schmerztherapie und individuelle Gesundheitsleistungen. Unsere Ärzte koordinieren Diagnostik und Therapie fachübergreifend – für eine sichere und schnelle medizinische Betreuung in Diez und Limburg."
      />

      <CTASection
        title="Jetzt Termin vereinbaren"
        subtitle="Vereinbaren Sie Ihren Termin online oder kontaktieren Sie uns bei akuten Beschwerden direkt."
        buttonText="Jetzt Termin vereinbaren"
      />
    </Layout>
  );
};

export default LeistungenPage;
